import style from './ErpDialog.module.scss';

import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import i18next from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Collapse } from '@work4all/components/lib/components/collapse';
import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { ChipList } from '@work4all/components/lib/dataDisplay/chip/ChipList';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { useOnClose } from '@work4all/components/lib/navigation/navigation-overlay';

import { useUser } from '@work4all/data';

import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { canEditERPStatus } from '@work4all/utils/lib/permissions';

import {
  Chip,
  LabeledInput,
} from '../../../containers/mask-overlays/locked-inputs';
import { ControlWrapper } from '../../../containers/mask-overlays/mask-overlay/components';
import { LockOverride } from '../../../containers/mask-overlays/mask-overlay/components/LockOverride';
import { MaskOverlayFullscreenToggleButton } from '../../../containers/mask-overlays/mask-overlay/components/MaskOverlayFullscreenToggleButton';
import { getRequiredEntity } from '../../../containers/mask-overlays/mask-overlay/views/erp/getRequiredEntity';
import { settings, useSetting } from '../../../settings';
import { PartnerPickerByEntity } from '../../entity-picker/PartnerPickerByEntity';
import { NavigationOverlayHeader } from '../../navigation-overlay-header/NavigationOverlayHeader';

import { ErpDialogByMode } from './components/ErpDialogByMode';
import { ErpDialogGroupingHeader } from './components/ErpDialogGroupingHeader';
import { OnItemsChangedOptions } from './components/ErpDialogPositions';
import { PositionCount } from './components/PositionCount';
import { useConversionSubmit } from './hooks/use-conversion-submit';
import { updatePositionCount } from './hooks/use-position-count';
import { ErpDialogProps, ErpObject, SelectedPosition } from './types';
import {
  calculateErpNumber,
  evaluateErpNumber,
  getInitialBusinessPartner,
  isContractToDeliveryNote,
  isContractToOrder,
  isGroupingEnabled,
  isSingleBusinessPartner,
} from './utils';

// We need to use it as shortcut before is always in german
const de = i18next.getFixedT('de');

export const ErpDialog = (props: ErpDialogProps) => {
  const {
    onClose,
    onConfirm,
    targetEntity,
    title,
    data,
    sourceEntity,
    isBusinessPartnerEditAllowed = false,
    description: inDescription,
  } = props;

  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );
  const [positionPatch, setPositionPatch] = useState<SelectedPosition[]>(null);

  const groupingEnabled = isGroupingEnabled(sourceEntity, targetEntity);
  const initialBusinessPartner = getInitialBusinessPartner(data);

  const [businessPartner, setBusinessPartner] = useState(
    initialBusinessPartner
  );
  const bpEntity = getRequiredEntity(targetEntity);

  const number = calculateErpNumber(sourceEntity, data);

  const note = data.length === 1 ? data[0]?.note : '';

  const convertDialogPositionMaximized = useSetting(
    settings.convertDialogPositionMaximized()
  );

  const {
    value: refreshHeadAndClosingText,
    set: setRefreshHeadAndClosingText,
  } = useSetting(settings.erpRefreshHeadAndClosingText());

  const singleBp = isSingleBusinessPartner(data);

  const [intoSingle, setIntoSingle] = useState(singleBp);
  const [historicalItems, setHistoricalItems] = useState(!groupingEnabled);
  const [freeze, setFreeze] = useState(data[0]?.frozen ?? false);
  const [close, setClose] = useState(data[0]?.isClosed ?? false);
  const [showBOM, setShowBOM] = useState(false);

  const [maximized, setMaximized] = useState<boolean>(
    data.length === 1 ? convertDialogPositionMaximized.value : false
  );
  const lastMaximized = useRef<boolean>(null);

  const handleMaximized = useCallback(
    (input: boolean) => {
      if (data.length === 1) lastMaximized.current = input;
      setMaximized(input);
    },
    [data.length]
  );

  useOnClose(() => {
    if (data.length === 1 && lastMaximized.current !== null)
      convertDialogPositionMaximized.set(lastMaximized.current);
    onClose();
  }, []);

  // calculate description
  const erpConvertionOriginalName = useSetting(
    settings.erpConvertionOriginalName()
  );
  const [description, setDescription] = useState(note);
  useEffect(() => {
    if (inDescription) {
      setDescription(inDescription);
    } else if (erpConvertionOriginalName.value) {
      setDescription(
        `${de(
          `COMMON.ERP.ABBREVIATION.${sourceEntity.toUpperCase()}`
        )}${number} | ${note?.split('\n')?.[0]}`
      );
    }
  }, [
    erpConvertionOriginalName.value,
    inDescription,
    note,
    number,
    sourceEntity,
    t,
  ]);

  const originalName = description?.startsWith(
    `${de(`COMMON.ERP.ABBREVIATION.${sourceEntity.toUpperCase()}`)}${number} | `
  );

  // special cases
  const contractToOrder = isContractToOrder(sourceEntity, targetEntity);

  const erpConversionGroupByType = useSetting(
    settings.erpConversionGroupByType()
  );

  const groupByType = groupingEnabled ? erpConversionGroupByType.value : 'NONE';

  const [passThrough, setPassThrough] = useState(groupByType === 'CONTRACT');

  const passThroughEnabled = passThrough || groupByType === 'CONTRACT';

  useEffect(() => {
    updatePositionCount(
      Object.values(positions.current).flatMap((x) => x),
      passThroughEnabled
    );
  }, [passThroughEnabled]);

  // Group by Order id or supplier id
  const positions = useRef<Record<number, SelectedPosition[]>>({});
  const onItemsChanged = useCallback(
    (id: number, items: SelectedPosition[], options: OnItemsChangedOptions) => {
      // Items sometime are in Other Supplier so we need to remove it from other grouping as only one group is in update
      // Remove items from other groups before assigning to the current group
      const uniqueIds = items.map(
        (item) => item.position.id ?? item.position.originalId
      );

      Object.entries(positions.current).forEach(([groupId, groupItems]) => {
        if (Number(groupId) !== id) {
          positions.current[groupId] = groupItems.filter(
            (item) =>
              !uniqueIds.includes(item.position.id ?? item.position.originalId)
          );
        }
      });

      positions.current[id] = items;
      updatePositionCount(
        Object.values(positions.current).flatMap((x) => x),
        passThroughEnabled
      );
      const RELEVANT_FOR_DISABLED: ErpPositionsKind[] = [
        ErpPositionsKind.STANDARD,
        ErpPositionsKind.STUECKLISTE,
      ];

      const relevantForDisabledPositions = Object.values(positions.current)
        ?.flatMap((x) => x)
        .filter((x) => RELEVANT_FOR_DISABLED.includes(x.position.positionKind));

      const notSelectedAtall = relevantForDisabledPositions.every(
        (x) => !x.selected
      );

      const noSomeSupplier = relevantForDisabledPositions.some(
        (x) => !x.position.supplier?.id && x.selected
      );

      setDisabled(
        (notSelectedAtall && !options.forceEnabled) ||
          (contractToOrder && noSomeSupplier)
      );
    },
    [contractToOrder, passThroughEnabled]
  );

  // special cases
  const contractToDeliveryNote = isContractToDeliveryNote(
    sourceEntity,
    targetEntity
  );

  const user = useUser();

  const setGroupByType = erpConversionGroupByType.set;
  const showShowSourceOptions = shouldDisplaySourceOptions(
    sourceEntity,
    targetEntity,
    initialBusinessPartner?.__typename,
    businessPartner?.__typename,
    data
  );
  const disableShowSourceOptions = shouldDisableSourceOptions(
    sourceEntity,
    targetEntity,
    data
  );

  const conversionSubmit = useConversionSubmit({
    description,
    groupByType,
    historicalItems,
    intoSingle,
    positions: positions.current,
    onConfirm,
    setDisabled,
    data,
    contractToDeliveryNote,
    close,
    disableShowSourceOptions,
    freeze,
    showShowSourceOptions,
    refreshHeadAndClosingText,
    passThroughEnabled,
    targetEntity,
  });

  const handleSubmit = async () => {
    await conversionSubmit();

    if (data.length === 1)
      convertDialogPositionMaximized.set(lastMaximized.current);
    erpConvertionOriginalName.set(originalName);
  };

  const canEditStatus = canEditERPStatus(user);

  const renderTargetOptions = () => (
    <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'row' }}>
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        {!props.description && (
          <CheckboxRadioItem
            label={t('COMMON.ERP.CONVERT.ORIGINAL_OBJECT_IN_NAME')}
            control="checkbox"
            checked={originalName}
            onChange={(_event: unknown, value: boolean) => {
              if (value) {
                setDescription(
                  `${de(
                    `COMMON.ERP.ABBREVIATION.${sourceEntity.toUpperCase()}`
                  )}${number} | ${description}`
                );
              } else {
                setDescription(
                  description.replace(
                    `${de(
                      `COMMON.ERP.ABBREVIATION.${sourceEntity.toUpperCase()}`
                    )}${number} | `,
                    ''
                  )
                );
              }
            }}
          />
        )}
        {groupByType === 'NONE' && (
          <CheckboxRadioItem
            label={t('COMMON.ERP.CONVERT.HISTORICAL_ITEMS')}
            control="checkbox"
            checked={historicalItems}
            onChange={(_event: unknown, value: boolean) => {
              setHistoricalItems(value);
            }}
          />
        )}
        {contractToDeliveryNote && (
          <CheckboxRadioItem
            label={t('COMMON.ERP.CONVERT.SHOW_BOM')}
            control="checkbox"
            checked={showBOM}
            onChange={(_event: unknown, value: boolean) => {
              setShowBOM(value);
            }}
          />
        )}
        <CheckboxRadioItem
          label={t('COMMON.ERP.CONVERT.UPDATE_HEADER_FOOTER_TEXT')}
          control="checkbox"
          checked={refreshHeadAndClosingText}
          onChange={(_, value: boolean) => {
            setRefreshHeadAndClosingText(value);
          }}
        />
      </div>
      {groupByType !== 'NONE' && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
          }}
        >
          <CheckboxRadioItem
            label={t('COMMON.ERP.PASS_THROUGH')}
            control="checkbox"
            disabled={groupByType === 'CONTRACT'}
            checked={passThroughEnabled}
            onChange={(_, value: boolean) => {
              setPassThrough(value);
            }}
          />
        </div>
      )}
    </div>
  );

  const onRefresh = useCallback((context: { position: Position }) => {
    const flat = Object.values(positions.current).flatMap((x) => x);

    // We need to sync suppliers on the same articles
    if (context?.position) {
      const current = flat.find((x) => x.position.id === context.position.id);
      flat.forEach((x) => {
        if (x.position.articleId === current.position.articleId) {
          if ('supplier' in context.position) {
            x.position.supplier = context.position.supplier;
            x.position.supplierId = context.position.supplier?.id;
          } else if ('discount' in context.position) {
            x.position.discount = context.position.discount;
          } else if ('singlePriceNet' in context.position) {
            x.position.singlePriceNet = context.position.singlePriceNet;
          }
        }
      });
    }

    setPositionPatch(flat);
  }, []);

  return (
    <>
      <NavigationOverlayHeader
        title={title}
        breadcrumbsChildren={
          <Box
            display="flex"
            gap="0.5rem"
            justifyContent="flex-end"
            alignItems="center"
          >
            {groupByType !== 'NONE' && <PositionCount />}
            <Button
              color="primary"
              size="large"
              variant="contained"
              onClick={handleSubmit}
              disabled={disabled}
            >
              {sourceEntity === targetEntity
                ? t('COMMON.CLONE')
                : t('MASK.CONVERT')}
            </Button>
            <MaskOverlayFullscreenToggleButton />
          </Box>
        }
      ></NavigationOverlayHeader>

      <Box paddingTop="1rem" flex={1} display="flex" flexDirection="column">
        <Collapse
          defaultOpen
          title={t('COMMON.GENERAL')}
          headerBar={
            groupingEnabled && (
              <div className={style.collapse}>
                <div className={style.collapseInner}>
                  <ErpDialogGroupingHeader
                    value={groupByType}
                    onRefresh={onRefresh}
                    onChange={(groupType) => {
                      positions.current = {};
                      setGroupByType(groupType);
                    }}
                  />
                </div>
              </div>
            )
          }
        >
          <Stack flexDirection={maximized ? 'row' : 'column'} gap="1rem">
            {data.length === 1 && (
              <LabeledInput
                disabled
                value={data[0]?.note}
                label={`${t(`COMMON.${sourceEntity.toUpperCase()}`)} ${number}`}
              />
            )}
            {data.length > 1 && (
              <LabeledInput
                disabled
                value={
                  <ChipList>
                    {data.map((item, idx) => {
                      return (
                        <Chip
                          key={idx}
                          withSeperator={false}
                          maxWidth={false}
                          label={`${evaluateErpNumber(sourceEntity, item)}`}
                          disabled
                        />
                      );
                    })}
                  </ChipList>
                }
                label={t(`COMMON.${sourceEntity.toUpperCase()}_other`)}
              />
            )}

            {!groupingEnabled && singleBp && (
              <LockOverride forceLock={!isBusinessPartnerEditAllowed}>
                <PartnerPickerByEntity
                  entity={bpEntity}
                  businessPartner={businessPartner}
                  onChange={(array) => {
                    setBusinessPartner(array[0]);
                  }}
                />
              </LockOverride>
            )}
          </Stack>
        </Collapse>
        {!groupingEnabled && data?.length > 1 && (
          <Collapse defaultOpen title={t('COMMON.ERP.CONVERT.TYPE')}>
            <RadioGroup
              defaultValue="1"
              value={intoSingle}
              onChange={(_event, value) => {
                setIntoSingle(value === 'true');
              }}
            >
              <FormControlLabel
                value={true}
                disabled={!singleBp}
                control={<Radio />}
                label={t(
                  `COMMON.ERP.CONVERT.ONE.${targetEntity.toUpperCase()}`
                )}
              />
              <FormControlLabel
                value={false}
                disabled={!singleBp}
                control={<Radio />}
                label={t('COMMON.ERP.CONVERT.MULTI', {
                  object: t(`COMMON.${targetEntity.toUpperCase()}_other`),
                })}
              />
            </RadioGroup>
          </Collapse>
        )}

        <Collapse defaultOpen title={t('COMMON.SETTINGS')}>
          {(data.length === 1 || intoSingle) && (
            <ControlWrapper>
              <LabeledInput
                label={t('INPUTS.DESCRIPTION')}
                value={description}
                onChange={(e) => {
                  setDescription(e.currentTarget.value);
                }}
              />
            </ControlWrapper>
          )}

          {showShowSourceOptions && (
            <Stack
              display="grid"
              gridTemplateColumns={isMobile || !maximized ? '1fr' : '1fr 1fr'}
              gap="1rem"
            >
              <Stack>
                <Divider title={t(`COMMON.${sourceEntity.toUpperCase()}`)} />
                <Stack flexDirection="row" gap="1.25rem">
                  <Tooltip
                    title={
                      !canEditStatus
                        ? t('ERROR.NO_RIGHT_TO_EDIT_ERP_STATUS')
                        : ''
                    }
                    activateForDisabled
                    placement="top"
                  >
                    <CheckboxRadioItem
                      label={t('COMMON.ERP.FREEZE')}
                      control="checkbox"
                      checked={freeze}
                      disabled={disableShowSourceOptions || !canEditStatus}
                      onChange={(_event: unknown, value: boolean) => {
                        setFreeze(value);
                        if (value === false) {
                          setClose(false);
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip
                    title={
                      !canEditStatus
                        ? t('ERROR.NO_RIGHT_TO_EDIT_ERP_STATUS')
                        : ''
                    }
                    activateForDisabled
                    placement="top"
                  >
                    <CheckboxRadioItem
                      label={t('COMMON.ERP.CLOSE')}
                      control="checkbox"
                      checked={close}
                      disabled={disableShowSourceOptions || !canEditStatus}
                      onChange={(_event: unknown, value: boolean) => {
                        setClose(value);
                        if (value === true) {
                          setFreeze(true);
                        }
                      }}
                    />
                  </Tooltip>
                </Stack>
              </Stack>
              <Stack>
                <Divider title={t(`COMMON.${targetEntity.toUpperCase()}`)} />
                <Stack flexDirection="row" gap="1.25rem">
                  {renderTargetOptions()}
                </Stack>
              </Stack>
            </Stack>
          )}

          {!showShowSourceOptions && renderTargetOptions()}
        </Collapse>
        <ErpDialogByMode
          sourceEntity={sourceEntity}
          targetEntity={targetEntity}
          onToggled={handleMaximized}
          maximizedMask={maximized}
          onItemsChanged={onItemsChanged}
          data={data}
          groupByType={groupByType}
          positionPatch={positionPatch}
          showBOM={showBOM}
          onRefresh={onRefresh}
          historicalItems={historicalItems}
          isEditMode={groupByType === 'NONE' ? true : !passThroughEnabled}
        />
      </Box>
      {groupByType !== 'NONE' && (
        <div style={{ borderTop: '1px solid var(--ui04)', padding: '1rem' }}>
          <Typography variant="caption" color="text.secondary">
            {t('COMMON.ERP.CONVERT.INFO_1')}
          </Typography>
        </div>
      )}
    </>
  );
};

const shouldDisplaySourceOptions = (
  sourceEntity: Entities,
  targetEntity: Entities,
  sourceBpType?: string,
  targetBpType?: string,
  erpData?: ErpObject[]
) => {
  const hasMultiple = erpData?.length > 1;
  const isSomeFrozen = hasMultiple && erpData?.find((x) => x.frozen);
  const isSomeClosed = hasMultiple && erpData?.find((x) => x.isClosed);

  if (isSomeFrozen || isSomeClosed) {
    return false;
  }

  if (sourceEntity === Entities.demand && targetEntity === Entities.order) {
    return true;
  }

  if (sourceEntity === Entities.offer) {
    if (sourceBpType === targetBpType) {
      return true;
    }
    return false;
  }

  if (targetEntity === Entities.invoice) {
    if (sourceEntity === Entities.ra) {
      return false;
    }
    if (sourceBpType === targetBpType) {
      return true;
    }
    return false;
  }

  return false;
};

const shouldDisableSourceOptions = (
  sourceEntity: Entities,
  targetEntity: Entities,
  erpData?: ErpObject[]
) => {
  if (sourceEntity === targetEntity) {
    return true;
  }

  const isFrozen = erpData?.[0]?.frozen;
  const isClosed = erpData?.[0]?.isClosed;

  if (isFrozen || isClosed) {
    return true;
  }

  return false;
};
